import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { WifiOff } from "lucide-react";
import { color, motion as motionTokens, space } from "@tokens/index";
import { Text } from "@primitives/Text";
import type { RealtimeIndicatorState } from "./RealtimeStatusIndicator";

/**
 * "Live updates are off" banner: a flat warning strip under the top bar.
 *
 * Pure presentation. The caller passes the connection state; this component
 * owns no subscription. The strip opens only after the state has stayed not
 * `connected` for `delayMs`, so a short blip (the hourly reconnect at token
 * expiry, about 3 s) never shows it. A return to `connected` closes it at
 * once. It also covers a tab that never connected: the header dot stays
 * hidden until the first `connected`, so this strip is then the only sign.
 *
 * Status is color, glyph, and words. The strip is flat: no shadow, no
 * button, no dismiss.
 *
 * A visually hidden live region is always mounted. It holds the text only
 * while the strip is open: a region that gets its text later is announced
 * reliably, a region that mounts with text is not. The visible strip is
 * `aria-hidden`, so the text is not read twice.
 */

/** Default for `delayMs`: how long the state must stay not `connected` before the banner opens. */
export const REALTIME_BANNER_DELAY_MS = 5_000;

const MESSAGE = "Live updates are off. Reconnecting. Queues and the open workflow keep refreshing.";

export interface RealtimeStatusBannerProps {
  readonly state: RealtimeIndicatorState;
  /** Default `REALTIME_BANNER_DELAY_MS`. */
  readonly delayMs?: number;
  /** Horizontal padding. Default `space[6]`. Pass the page gutter to line up with the content. */
  readonly gutter?: string;
}

/**
 * `true` when the connection has been down for `delayMs`. The timer follows
 * `connected` only: a move between `reconnecting` and `disconnected` does
 * not restart it. The result is `false` in the same render that `connected`
 * returns.
 */
function useDownLongEnough(connected: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (connected) {
      setElapsed(false);
      return;
    }
    const timer = setTimeout(() => {
      setElapsed(true);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [connected, delayMs]);
  return !connected && elapsed;
}

const timing = `${motionTokens.control.toggle} ${motionTokens.easings.standard}`;

// Height and fade, CSS only. The row goes from `0fr` to `1fr`, so it grows to
// the height of the strip; the clip child needs `min-height: 0` so the row can
// shrink to zero. The global reduced-motion rule is `!important`, so it wins
// over this inline transition: the height then snaps and only the fade runs
// (100 ms). `flex-shrink: 0` stops a flex column parent from squeezing it.
function rootStyle(open: boolean): CSSProperties {
  return {
    display: "grid",
    gridTemplateRows: open ? "1fr" : "0fr",
    opacity: open ? 1 : 0,
    flexShrink: 0,
    transition: `grid-template-rows ${timing}, opacity ${timing}`,
  };
}

const clipStyle: CSSProperties = {
  overflow: "hidden",
  minHeight: 0,
};

function stripStyle(gutter: string): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    paddingBlock: space[2],
    paddingInline: gutter,
    background: color.status.warningBg,
    borderBottom: `1px solid ${color.border.subtle}`,
  };
}

export function RealtimeStatusBanner({
  state,
  delayMs = REALTIME_BANNER_DELAY_MS,
  gutter = space[6],
}: RealtimeStatusBannerProps): ReactElement {
  const open = useDownLongEnough(state === "connected", delayMs);

  return (
    <div data-realtime-banner="" data-open={open ? "" : undefined} style={rootStyle(open)}>
      <span role="status" aria-live="polite" data-visually-hidden="">
        {open ? MESSAGE : null}
      </span>
      <div style={clipStyle}>
        <div aria-hidden="true" style={stripStyle(gutter)}>
          <WifiOff
            size={16}
            strokeWidth={2.25}
            color={color.status.warningFg}
            style={{ flexShrink: 0 }}
          />
          <Text role="bodySm">{MESSAGE}</Text>
        </div>
      </div>
    </div>
  );
}
